/**
 * Ce que l'avocat lit avant que le dock ne laisse la place à la rédaction (#1588).
 *
 * La bascule est forcée — Hector écrit, l'écran suit — mais elle ne doit plus
 * être muette : sans un mot, l'avocat voyait sa page de réglages disparaître au
 * milieu d'une phrase et cherchait ce qu'il avait cliqué. Le bandeau nomme la
 * cause (« Hector rédige ») et l'effet (« ouverture de la rédaction en grand »),
 * puis laisse la minuterie du hook faire le reste.
 *
 * **En flux, jamais en surimpression.** Même règle que `CanvasEcritureBandeau` :
 * il s'insère au-dessus du chat et le pousse, plutôt que de recouvrir une
 * surface que l'avocat est peut-être en train de lire. Le fondu du dock vit
 * chez l'hôte (`DossierCaseDiscoveryShell`) et porte sur la conversation, pas
 * sur ce bandeau — un texte qui s'efface pendant qu'on le lit ne se lit pas, et
 * un bouton estompé ne se clique pas.
 *
 * Aucun émoji : règle produit des bannières Hector.
 */

import type { JSX } from 'react';
import { PenLine } from 'lucide-react';
import { Button } from '@/components/ui/button';

/** Le texte de l'annonce, partagé avec les stories qui l'attendent. */
export const ANNONCE_DE_BASCULE_DU_DOCK = 'Hector rédige — ouverture de la rédaction en grand…';

/** Le raccourci pour qui ne veut pas attendre le délai. */
export const OUVRIR_MAINTENANT_LABEL = 'Ouvrir maintenant';

interface BandeauDeBasculeDuDockProps {
  /** Coupe l'attente et bascule tout de suite. */
  readonly onOuvrirMaintenant: () => void;
}

export function BandeauDeBasculeDuDock({
  onOuvrirMaintenant,
}: BandeauDeBasculeDuDockProps): JSX.Element {
  return (
    <div
      // `role="status"` plutôt qu'une région live ajoutée ailleurs : le bandeau
      // apparaît en même temps que son contenu, et c'est précisément ce que
      // cette annonce doit dire — l'écran va changer sans geste de l'avocat.
      role="status"
      data-testid="bandeau-bascule-dock"
      className="flex shrink-0 items-center gap-2 border-b border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-900"
    >
      <PenLine className="h-3.5 w-3.5 shrink-0 text-amber-700" aria-hidden="true" />
      <span className="min-w-0">{ANNONCE_DE_BASCULE_DU_DOCK}</span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="ml-auto h-6 shrink-0 px-2 text-xs text-amber-900 hover:bg-amber-100"
        onClick={onOuvrirMaintenant}
      >
        {OUVRIR_MAINTENANT_LABEL}
      </Button>
    </div>
  );
}
